// Updated FilterSection that allows all filters to be removable
import React, { useState, useEffect, useRef } from 'react';
import { Popover, Input, Space, Button, Switch } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import FilterValues from './FilterValues';
import FilterPill from './FilterPill';
import RangeSlider from './RangeSlider';

const AnimatedPill = ({ children, onRemove }) => {
    const [isExiting, setIsExiting] = useState(false);

    const handleRemove = (e) => {
        e.stopPropagation();
        setIsExiting(true);
        setTimeout(onRemove, 200); // Match the transition duration
    };

    return (
        <div style={{
            opacity: isExiting ? 0 : 1,
            transform: isExiting ? 'scale(0.95)' : 'scale(1)',
            transition: 'all 0.2s ease',
        }}>
            {React.cloneElement(children, { onClick: handleRemove })}
        </div>
    );
};

// Replace the SelectedValuesDisplay in FilterSection.js

const SelectedValuesDisplay = ({ type, values, selectedValues, onSelectionChange, onOpenFilter }) => {
    if (!selectedValues || selectedValues.length === 0) {
        return (
            <Space size={8} style={{ marginLeft: '8px' }}>
                <FilterPill
                    label="Any"
                    style={{ backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none', cursor: 'pointer' }}
                    showRemoveButton={false}
                    onClick={onOpenFilter} // Open the filter when clicked
                />
            </Space>
        );
    }

    const handleRemoveValue = (valueToRemove) => {
        if (type === 'enum') {
            const valueToRemoveId = values.find(v => v.label === valueToRemove)?.value;
            if (valueToRemoveId !== undefined) {
                const newValues = selectedValues.filter(v => v !== valueToRemoveId);
                onSelectionChange(newValues);
            }
        } else {
            onSelectionChange([]);
        }
    };

    const getDisplayValue = () => {
        switch (type) {
            case 'number':
                return [`${selectedValues[0]} - ${selectedValues[1]}`];
            case 'boolean':
                return [selectedValues[0] ? 'Yes' : 'No'];
            case 'text':
                return [selectedValues[0]];
            case 'enum':
                const selectedLabels = values
                    .filter(v => selectedValues.includes(v.value))
                    .map(v => v.label);

                if (selectedLabels.length <= 3) {
                    return selectedLabels;
                }

                return [
                    ...selectedLabels.slice(0, 2),
                    `+${selectedLabels.length - 2} more`
                ];
            default:
                return selectedValues;
        }
    };

    const displayValue = getDisplayValue();
    return (
        <Space size={8} style={{ marginLeft: '4px' }}>
            {displayValue.map((value, index) => (
                <FilterPill
                    key={value}
                    label={value}
                    active={true}
                    showRemoveButton={true}
                    onClick={onOpenFilter} // Open the filter when the pill is clicked
                    onRemove={() => handleRemoveValue(value)} // Remove only when X is clicked
                />
            ))}
        </Space>
    );
};


const FilterSection = ({
                           title,
                           type = 'enum',
                           values = [],
                           selectedValues = [],
                           onSelectionChange,
                           loading = false,
                           hasMore = false,
                           onLoadMore,
                           searchable = false,
                           expanded = false,
                           onExpandedChange,
                           filter,
                           onRemove
                       }) => {
    const [searchText, setSearchText] = useState('');
    const [localValues, setLocalValues] = useState(values);
    const [tempSelectedValues, setTempSelectedValues] = useState(selectedValues);

    const handleSearch = (value) => {
        setSearchText(value);
        if (!value) {
            setLocalValues(values);
        } else {
            setLocalValues(values.filter(v =>
                String(v.label).toLowerCase().includes(value.toLowerCase())
            ));
        }
    };

    const handleVisibilityChange = (visible) => {
        onExpandedChange?.(visible);
    };

    const handleApply = () => {
        onSelectionChange(tempSelectedValues);
        onExpandedChange(false);
    };

    const handleCancel = () => {
        //setTempSelectedValues(selectedValues);
        onExpandedChange(false);
    };

    // Open the filter dropdown
    const handleOpenFilter = () => {
        onExpandedChange?.(true);
    };

    // Initialize range values to full range when first mounted
    useEffect(() => {
        if (type === 'number' && (!tempSelectedValues || tempSelectedValues.length === 0)) {
            const min = Math.min(...values.map(v => v.value));
            const max = Math.max(...values.map(v => v.value));
            setTempSelectedValues([min, max]);
            onSelectionChange([min, max]);
        }
    }, [type, values]);


    const previousExpandedRef = useRef(expanded);

    useEffect(() => {
        // Only run when expanded transitions from true to false
        if (previousExpandedRef.current && !expanded) {
            setTempSelectedValues(selectedValues || []);
            setSearchText('');
        }
        // Update the ref
        previousExpandedRef.current = expanded;
    }, [expanded, selectedValues]);

    const content = (
        <div style={{ width: '400px' }}>
            {searchable && (
                <div style={{ marginBottom: '16px' }}>
                    <Input
                        prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </div>
            )}

            <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
                {type === 'number' && (
                    <div style={{ padding: '0 16px' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '12px',
                            paddingTop: '4px',
                            color: '#6b7280',
                            fontSize: '14px'
                        }}>
                            <span>{Math.min(...values.map(v => v.value))}</span>
                            <div style={{
                                color: '#10b981',
                                fontWeight: '500'
                            }}>
                                {tempSelectedValues[0]} - {tempSelectedValues[1]}
                            </div>
                            <span>{Math.max(...values.map(v => v.value))}</span>
                        </div>
                        <RangeSlider
                            min={Math.min(...values.map(v => v.value))}
                            max={Math.max(...values.map(v => v.value))}
                            value={tempSelectedValues}
                            onChange={setTempSelectedValues}
                        />
                    </div>
                )}
                {type === 'text' && (
                    <Input
                        value={tempSelectedValues[0] || ''}
                        onChange={(e) => setTempSelectedValues([e.target.value])}
                        placeholder="Enter text to filter..."
                    />
                )}
                {type === 'boolean' && (
                    <div style={{ padding: '8px' }}>
                        <Switch
                            checked={tempSelectedValues[0] || false}
                            onChange={(checked) => setTempSelectedValues([checked])}
                            style={{ backgroundColor: tempSelectedValues[0] ? '#10b981' : undefined }}
                        />
                        <span style={{ marginLeft: '8px', color: '#374151' }}>
              {tempSelectedValues[0] ? 'Yes' : 'No'}
            </span>
                    </div>
                )}
                {type === 'enum' && (
                    <FilterValues
                        type={type}
                        values={localValues}
                        selectedValues={tempSelectedValues}
                        onSelectionChange={setTempSelectedValues}
                        loading={loading}
                    />
                )}

                {hasMore && (
                    <Button
                        type="link"
                        onClick={onLoadMore}
                        style={{ padding: '8px 0' }}
                    >
                        Show more...
                    </Button>
                )}
            </div>

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid #f0f0f0',
                paddingTop: '16px'
            }}>
                <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => {
                        handleCancel();
                        onRemove?.();
                    }}
                >
                    Remove filter
                </Button>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button type="primary" onClick={handleApply}>
                        Apply
                    </Button>
                </div>
            </div>
        </div>
    );

    const triggerElement = (
        <div
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: '32px', // Set a consistent height for alignment
                cursor: 'pointer'
            }}
            onClick={handleOpenFilter} // Opening the filter happens on the container
        >
            <span style={{
                fontWeight: '500',
                color: '#374151',
                marginRight: '6px'
            }}>
                {title}:
            </span>
            <SelectedValuesDisplay
                type={type}
                values={values}
                selectedValues={selectedValues}
                onSelectionChange={onSelectionChange}
                onOpenFilter={handleOpenFilter}
            />
        </div>
    );

    return (
        <div className="filter-section" style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Popover
                content={content}
                trigger="click"
                placement="bottomLeft"
                open={expanded}
                onOpenChange={handleVisibilityChange}
            >
                {triggerElement}
            </Popover>
        </div>
    );
};

export default FilterSection;
